import React, { useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { AlertTriangle, RotateCcw, Trash2 } from 'lucide-react';

export const DangerZone: React.FC = () => {
  const { role } = useAuth();
  const isSuper = role?.key === 'super_admin';
  const [busy, setBusy] = useState(false);

  const wipe = async (): Promise<void> => {
    if (!confirm('Wipe all test data? Keeps super admin, built-in roles, INS-001, default settings.')) return;
    setBusy(true);
    try { const r = await api.post('/danger/wipe-test-data'); toast.success('Wiped: ' + (r.data.wiped || []).join(', ')); }
    catch (err: unknown) { toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Failed'); }
    finally { setBusy(false); }
  };
  const regen = async (): Promise<void> => {
    if (!confirm('Rotate ALL broker registration links? All old links stop working immediately.')) return;
    setBusy(true);
    try { const r = await api.post('/danger/regenerate-all-broker-links'); toast.success(`Rotated ${r.data.regenerated} links`); }
    catch { toast.error('Failed'); }
    finally { setBusy(false); }
  };

  if (!isSuper) return (
    <Card><CardHeader><CardTitle className="flex items-center gap-2"><AlertTriangle size={18} className="text-red-500" /> Danger Zone</CardTitle></CardHeader>
      <CardContent>Only Super Admin can access this area.</CardContent></Card>
  );

  return (
    <div className="space-y-4">
      <Card className="border-red-200">
        <CardHeader><CardTitle className="flex items-center gap-2 text-red-600"><AlertTriangle size={18} /> Danger Zone</CardTitle>
          <CardDescription>Destructive operations. Super Admin only. All actions are logged.</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          <div className="p-3 border border-border/60 rounded-md flex items-center justify-between">
            <div>
              <div className="font-semibold text-raptor-navy">Wipe test data</div>
              <div className="text-xs text-raptor-navy/60 font-body">Removes brokers, staff, clients, policies, sites, comms/OTP/access logs. Keeps super admin + built-in roles + INS-001 + default settings.</div>
            </div>
            <Button variant="destructive" onClick={wipe} disabled={busy}><Trash2 size={14} /> Wipe</Button>
          </div>
          <div className="p-3 border border-border/60 rounded-md flex items-center justify-between">
            <div>
              <div className="font-semibold text-raptor-navy">Regenerate all broker links</div>
              <div className="text-xs text-raptor-navy/60 font-body">Rotates every broker&apos;s registration token. Previously issued links stop working immediately. Emails to the affected brokers are not sent automatically. Please notify them by another channel.</div>
            </div>
            <Button variant="outline" onClick={regen} disabled={busy}><RotateCcw size={14} /> Rotate all</Button>
          </div>
          <div className="p-3 border border-border/60 rounded-md opacity-60">
            <div className="font-semibold text-raptor-navy">Reset all user 2FA seeds</div>
            <div className="text-xs text-raptor-navy/60 font-body">Available once time-based one-time PIN (TOTP) sign-in is enabled.</div>
          </div>
          <div className="p-3 border border-border/60 rounded-md opacity-60">
            <div className="font-semibold text-raptor-navy">Export tenant data (JSON dump)</div>
            <div className="text-xs text-raptor-navy/60 font-body">Available in a future release.</div>
          </div>
          <div className="p-3 border border-red-200 rounded-md bg-red-50">
            <div className="font-semibold text-red-700">Delete tenant (production)</div>
            <div className="text-xs text-red-700/70 font-body">Blocked in this environment. This action can only be performed in the production tenant, and requires a typed confirmation.</div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
